import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import { Program } from '../../Reports/NewReportDialog';
import { AllocationReports } from './allocationLocalStorage';
import {
  mapReportPrograms,
  ReportWithProgramYears,
  tenYearsList,
} from '../../ReportsBeta/NewReport/NewReportContent';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  cloneReport,
  getReportsForProgram,
  ReportsForProgram,
  SuccessResponse,
} from '../../ReportsBeta/NewReport/api';
import { useClient } from 'shared/apollo';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { adjustStartAndEndDates } from '../../ReportsBeta/NewReport/utils';

interface AllocationCloneReportProps {
  reports: AllocationReports;
  selectedYear: number;
  selectedProgram: Program;
  investmentFinalSplitIndex: number;
  programsList: RequestState<{
    allPrograms: Program[];
  }>;
  handleNext: (report?: {
    year: number;
    reportId: number;
    programName: string;
  }) => unknown;
  setReports: React.Dispatch<React.SetStateAction<AllocationReports>>;
}

const StyledDiv = styled.div`
  margin: 0 auto;
  max-width: 488px;
  .allocation-clone-report {
    margin-bottom: ${SPACERS.tiny};
  }
  .dropdown-container {
    display: flex;
    gap: ${SPACERS.tiny};
    .program-dropdown {
      flex: 1;
    }
    .year-dropdown {
      width: 100px;
    }
  }
  .error-text {
    margin-top: ${SPACERS.medium};
  }
  button {
    min-width: 200px;
    margin: ${SPACERS.medium} auto;
  }
`;

const AllocationCloneReport: React.FC<AllocationCloneReportProps> = ({
  programsList,
  selectedYear,
  selectedProgram,
  handleNext,
}) => {
  const client = useClient();
  const [error, setError] = useState<boolean>(false);
  const [clonedReport, makeClonedReportRequest] = useGraphQLRequest<
    SuccessResponse<'newReport'>
  >();
  const [reportsForProgram, makeReportsForProgramRequest] = useGraphQLRequest<{
    reports: ReportsForProgram[];
  }>();
  const [selected, setSelected] = useState<{
    year: number | undefined;
    program: Program | undefined;
  }>({ year: undefined, program: selectedProgram });

  const reports: ReportWithProgramYears = useMemo(() => {
    if (reportsForProgram.type !== 'REQUEST_SUCCESS') return {};
    return mapReportPrograms(
      reportsForProgram.data?.reports?.filter(r => r.enabled),
    ).reportPrograms;
  }, [reportsForProgram]);

  const report =
    selected.program && selected.year
      ? reports[selected.program.Id]?.[selected.year]
      : undefined;

  const handleReportChange = useCallback(
    (year: number | undefined) => {
      setSelected(s => ({
        program: s.program,
        year,
      }));
    },
    [setSelected],
  );

  const handleProgramChange = useCallback(
    (program: Program | undefined) => {
      setSelected({
        program,
        year: undefined,
      });
    },
    [setSelected],
  );

  useEffect(() => {
    if (!client) return;
    reportsForProgram.type === 'REQUEST_INIT' &&
      makeReportsForProgramRequest(getReportsForProgram(client));
  }, [client]);

  useEffect(() => {
    if (clonedReport.type !== 'REQUEST_SUCCESS') return;
    const reportId = clonedReport.data.newReport.id;
    if (!reportId) {
      setError(true);
      return;
    }
    handleNext({
      reportId,
      year: selectedYear,
      programName: selectedProgram.name,
    });
  }, [clonedReport]);

  const handleClone = useCallback(() => {
    if (!client || !report || !selected.program || !selected.year) {
      setError(true);
      return;
    }
    setError(false);
    const dates = adjustStartAndEndDates(
      report.startDate,
      report.endDate,
      selected.year,
    );

    makeClonedReportRequest(
      cloneReport(
        client,
        report.reportId,
        selected.program,
        selectedYear,
        dates.startDate,
        dates.endDate,
        true,
      ),
    );
  }, [
    report,
    selected,
    selectedYear,
    client,
    makeClonedReportRequest,
    setError,
  ]);

  const programOptions = programsList.data?.allPrograms || [];

  return (
    <StyledDiv>
      <TrueImpactText variant="body-bold" className="allocation-clone-report">
        Choose a report to clone
      </TrueImpactText>
      <div className="dropdown-container">
        <div className="program-dropdown">
          <TrueImpactDropdown
            value={selected.program}
            options={programOptions}
            getId={p => p.Id}
            getLabel={p => p.name}
            native={true}
            onChange={handleProgramChange}
            onClear={undefined}
            getItemDisability={undefined}
            name="Please select your program"
            className="dropdown-select"
            renderValue={p => p.name || 'Please select your program'}
          />
        </div>
        <div className="year-dropdown">
          <TrueImpactDropdown
            disabled={!selected.program}
            value={selected.year}
            options={tenYearsList}
            getId={y => y}
            getLabel={y => y}
            getItemDisability={y =>
              !(selected.program && reports[selected.program.Id]?.[y])
            }
            native={true}
            onChange={handleReportChange}
            onClear={undefined}
            name="Year"
            className="dropdown-select"
          />
        </div>
      </div>
      {(clonedReport.type === 'REQUEST_ERROR' || error) && (
        <TrueImpactText color="critical" align="center" className="error-text">
          Unable to clone this report, please try again!
        </TrueImpactText>
      )}
      {reportsForProgram.type === 'REQUEST_ERROR' && (
        <TrueImpactText color="critical" align="center" className="error-text">
          Unable to load reports at this time, please try refreshing the page.
        </TrueImpactText>
      )}
      <TrueImpactButton
        disabled={!selected || !selected.year}
        isLoading={clonedReport.type === 'REQUEST_START'}
        onClick={handleClone}
      >
        Clone Report
      </TrueImpactButton>
    </StyledDiv>
  );
};

export default AllocationCloneReport;
